import { CONTACT_US_URL, type DistributiveOmit } from '@bank/utils';
import {
  Button,
  EmptyState,
  type EmptyStateProps,
} from '@storebrand-digital/pretzel';

import type { Language } from '../language';
import { EmptyStateSecondaryIllustration } from '@storebrand-digital/pretzel/illustrations';

export type ErrorLayoutProps = DistributiveOmit<
  EmptyStateProps,
  'heading' | 'description' | 'iconEllipseProps'
> &
  Partial<Pick<EmptyStateProps, 'heading' | 'description'>> & {
    language: Language;
  };

const copy = {
  no: {
    heading: 'Oops, noe gikk galt',
    description:
      'Prøv å last siden på nytt, eller kontakt support hvis problemet fortsetter.',
    contactSupport: 'Kontakt kundeservice',
  },
  en: {
    heading: 'Oops, something went wrong',
    description:
      'Try reloading the page, or contact support if the problem persists.',
    contactSupport: 'Contact customer service',
  },
} satisfies Record<
  Language,
  {
    heading: string;
    description: string;
    contactSupport: string;
  }
>;

export const ErrorLayout = ({
  language,
  heading,
  description,
  actions,
  illustration = EmptyStateSecondaryIllustration,
  headingAriaLevel = 1,
  ...rest
}: ErrorLayoutProps) => {
  const fallback = copy[language];

  return (
    <EmptyState
      {...rest}
      headingAriaLevel={headingAriaLevel}
      illustration={illustration}
      heading={heading ?? fallback.heading}
      description={description ?? fallback.description}
      actions={
        actions === undefined ? (
          <Button variant="subtle" as="a" href={CONTACT_US_URL}>
            {fallback.contactSupport}
          </Button>
        ) : (
          actions
        )
      }
    />
  );
};
